import React, { useState, useEffect } from 'react';
import { Settings, ArrowLeft, Save, RotateCcw, Download, Monitor, Bell, Keyboard, Database, Shield, FileText, CheckCircle, Clock, Globe, Palette, Volume2, Zap } from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const AgentSettingsManagement = () => {
  const [settingsData, setSettingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const fetchSettingsData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getAgentSettings('agent-001');
        if (response.success) {
          setSettingsData(response.data);
        } else {
          setError('Failed to load settings data');
        }
      } catch (err) {
        setError('An error occurred while fetching settings data.');
        console.error('Error fetching settings data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettingsData();
  }, []);

  if (loading) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading settings data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500 dark:text-red-400">Error: {error}</div>;
  }

  if (!settingsData) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">No settings data available.</div>;
  }

  const { agentSettings, availableLanguages, dateFormats, timeZones, soundPacks, settingsStats } = settingsData;
  const { general, display, notifications, keyboard, data, privacy, export: exportSettings } = agentSettings;

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'display', label: 'Display', icon: Monitor },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'keyboard', label: 'Keyboard', icon: Keyboard },
    { id: 'data', label: 'Data', icon: Database },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'export', label: 'Export', icon: FileText }
  ];

  const handleSaveAll = () => {
    // Mock save functionality
    //console.log('Saving all settings...');
    setHasChanges(false);
  };

  const handleResetToDefault = () => {
    // Mock reset functionality
    //console.log('Resetting to default settings...');
    setHasChanges(false);
  };

  const handleExportSettings = () => {
    // Mock export functionality
    //console.log('Exporting settings...');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Settings className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Settings
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Configure your system settings and preferences
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleExportSettings}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <Download className="h-4 w-4 inline mr-2" />
                Export
              </button>
              <button 
                onClick={handleResetToDefault}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <RotateCcw className="h-4 w-4 inline mr-2" />
                Reset to Default
              </button>
              <button 
                onClick={handleSaveAll}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  hasChanges 
                    ? 'text-white bg-blue-600 hover:bg-blue-700' 
                    : 'text-gray-400 bg-gray-200 dark:bg-gray-700 cursor-not-allowed'
                }`}
                disabled={!hasChanges}
              >
                <Save className="h-4 w-4 inline mr-2" />
                Save All
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Settings Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Settings</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{settingsStats.totalSettings}</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Settings className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Customized</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{settingsStats.customizedSettings}</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Default</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{settingsStats.defaultSettings}</p>
              </div>
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Settings className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Version</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{settingsStats.settingsVersion}</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Settings Categories */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                      ${activeTab === tab.id
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="p-6">
            {/* General Settings Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">General Settings</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Application</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Auto-save</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Automatically save your work</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={general.autoSave.enabled}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Every {general.autoSave.interval} seconds
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Auto-logout</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Automatically log out after inactivity</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={general.autoLogout.enabled}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            After {general.autoLogout.timeout} hours
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Startup Behavior</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">What to do when the application starts</p>
                        </div>
                        <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          <option value="restore_session">Restore last session</option>
                          <option value="new_session">Start new session</option>
                          <option value="ask">Ask me</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Language & Region</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Language
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          {availableLanguages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                              {lang.nativeName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Date Format
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          {dateFormats.map((format) => (
                            <option key={format.value} value={format.value}>
                              {format.label} ({format.example})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Time Format
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          <option value="12h">12-hour (AM/PM)</option>
                          <option value="24h">24-hour</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Timezone
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          {timeZones.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label} ({tz.offset})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Display Settings Tab */}
            {activeTab === 'display' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Display Settings</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Appearance</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Theme
                        </label>
                        <div className="flex space-x-2">
                          <button className={`px-3 py-2 rounded-md text-sm font-medium ${
                            display.theme === 'light' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}>
                            Light
                          </button>
                          <button className={`px-3 py-2 rounded-md text-sm font-medium ${
                            display.theme === 'dark' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}>
                            Dark
                          </button>
                          <button className={`px-3 py-2 rounded-md text-sm font-medium ${
                            display.theme === 'auto' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}>
                            Auto
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Font Size
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                          <option value="extra-large">Extra Large</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Density
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          <option value="compact">Compact</option>
                          <option value="comfortable">Comfortable</option>
                          <option value="spacious">Spacious</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Accessibility</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Animations</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Enable smooth transitions and animations</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={display.animations}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">High Contrast</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Increase contrast for better visibility</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={display.highContrast}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Settings Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notification Settings</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Email Notifications</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Email Notifications</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Receive notifications via email</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={notifications.email.enabled}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Ticket Updates</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Get notified about ticket changes</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={notifications.email.ticketUpdates}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">System Alerts</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Receive system-wide alerts</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={notifications.email.systemAlerts}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Sound Settings</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Sound Notifications</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Play sounds for notifications</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={notifications.sound.enabled}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Volume</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Notification sound volume</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Volume2 className="h-4 w-4 text-gray-400" />
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={notifications.sound.volume}
                            className="w-24"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400 w-8">
                            {notifications.sound.volume}%
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Sound Pack
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          {soundPacks.map((pack) => (
                            <option key={pack.id} value={pack.id}>
                              {pack.name} - {pack.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Keyboard Settings Tab */}
            {activeTab === 'keyboard' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Keyboard Shortcuts</h3>
                
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Enable Keyboard Shortcuts</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Allow keyboard shortcuts to work</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={keyboard.enableShortcuts}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Default Shortcuts</h4>
                    <div className="space-y-3">
                      {keyboard.shortcuts.map((shortcut) => (
                        <div key={shortcut.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h5 className="font-medium text-gray-900 dark:text-gray-100">{shortcut.name}</h5>
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                                {shortcut.key}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{shortcut.description}</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={shortcut.enabled}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Custom Shortcuts</h4>
                    <div className="space-y-3">
                      {keyboard.customShortcuts.map((shortcut) => (
                        <div key={shortcut.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h5 className="font-medium text-gray-900 dark:text-gray-100">{shortcut.name}</h5>
                              <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                                {shortcut.key}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{shortcut.description}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                              <Settings className="h-4 w-4" />
                            </button>
                            <input
                              type="checkbox"
                              checked={shortcut.enabled}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Data Settings Tab */}
            {activeTab === 'data' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Data Management</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Cache Settings</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Cache Size</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Maximum cache size for offline data</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            value={data.cacheSize}
                            className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">MB</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Auto Cleanup</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Automatically clean up old cache data</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={data.autoCleanup}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Data Retention</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Data Retention Period</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">How long to keep local data</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            value={data.dataRetention}
                            className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Settings Tab */}
            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Privacy Settings</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Data Collection</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Analytics</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Help improve the application with usage analytics</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={privacy.analytics}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Crash Reporting</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Send crash reports to help fix bugs</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={privacy.crashReporting}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Usage Tracking</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Track how you use the application</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={privacy.usageTracking}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Cookie Preferences</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Essential Cookies</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Required for basic functionality</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={privacy.cookiePreferences.essential}
                          disabled
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Functional Cookies</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Enable enhanced features</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={privacy.cookiePreferences.functional}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Analytics Cookies</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Help us understand usage patterns</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={privacy.cookiePreferences.analytics}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Export Settings Tab */}
            {activeTab === 'export' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Export Settings</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Export Configuration</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Export Format
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                          <option value="json">JSON</option>
                          <option value="csv">CSV</option>
                          <option value="pdf">PDF</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Include Attachments</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Include file attachments in exports</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={exportSettings.includeAttachments}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">Compression</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Compress exported files</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={exportSettings.compression}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Date Range</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={exportSettings.dateRange.start}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={exportSettings.dateRange.end}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentSettingsManagement;
